import { useNavigate } from "react-router-dom";
import BackButton from "../../../../assets/images/BUTTON_Back.svg";
import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  getMappingStatus,
  retryMappingBatch,
  fetchOutboxBatch,
} from "../../../../thunks/workflow";
import DataGridComponent from "../../../../components/DataGridComponent";
import moment from "moment";
import PaginationComponent from "../../../../components/PaginationComponent";
import {
  NOTIFICATION_MESSAGE,
  PAGINATION_LIMIT,
  BATCH_DETAILS_LIMIT,
} from "../../../../utils/constant";
import Loader from "../../../../components/Loader";
import { Tooltip, Button, Grid, Typography } from "@mui/material";
import { BatchDetailsDialog } from "./WorkflowMappingDialog";
import { showSnackbar } from "../../../../slice/snackbarSlice";

// --------------------- Table Columns ---------------------
const buildColumns = (handleRetry) => [
  {
    field: "batchId",
    headerName: "Batch ID",
    width: 210,
    sortable: false,
  },

  {
    field: "createdAt",
    headerName: "Created Date",
    width: 170,
    sortable: false,
    renderCell: (params) => (
      <Typography>
        {params?.row?.createdAt && moment(params.row.createdAt).isValid()
          ? moment(params.row.createdAt).format("DD/MM/YYYY HH:mm A")
          : "--"}
      </Typography>
    ),
  },
  {
    field: "completedAt",
    headerName: "Complete Date",
    width: 170,
    sortable: false,
    renderCell: (params) => (
      <Typography>
        {params.row.completedAt
          ? moment(params.row.completedAt).format("DD/MM/YYYY HH:mm A")
          : "--"}
      </Typography>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    width: 130,
    sortable: false,
  },
  {
    field: "Source_Workflow",
    headerName: "Source Workflow",
    width: 280,
    sortable: false,
    renderCell: (params) => (
      <Typography>{params?.row?.sourceWorkflow?.name}</Typography>
    ),
  },
  {
    field: "Target_Workflow",
    headerName: "Target Workflow",
    width: 280,
    sortable: false,
    renderCell: (params) => (
      <Typography>{params?.row?.targetWorkflow?.name}</Typography>
    ),
  },

  {
    field: "action",
    headerName: "Action",
    width: 100,
    sortable: false,
    renderCell: (params) =>
      params?.row?.processedCount !== params?.row?.totalCount &&
      params?.row?.status === "COMPLETED_WITH_FAILURES" && (
        <Button
          onClick={() => handleRetry(params.row.batchId)}
          className="save_button"
        >
          Retry
        </Button>
      ),
  },
];

// --------------------- Main Component ---------------------
const WorkflowMappingStatus = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filterPayload, setFilterPayload] = useState({
    page: 1,
    limit: PAGINATION_LIMIT,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [isDialogLoading, setIsDialogLoading] = useState(false);
  const [batchId, setBatchID] = useState(null);

  // -------------------- Fetch List -----------------------
  const handleGetStatusMapping = useCallback(async () => {
    try {
      setLoading(true);
      const res = await dispatch(getMappingStatus(filterPayload)).unwrap();
      if (res) {
        setData(res.batches);
        setTotalCount(res.pagination.totalCount);
        dispatch(
          showSnackbar({
            show: true,
            message: NOTIFICATION_MESSAGE.MAPPING_QUEUE_FETCH_MSG,
            severity: "success",
          })
        );
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `${error?.message}`,
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [filterPayload, dispatch]);

  // ---------------- Fetch Batch Details -------------------

  const fetchOutBoxDetails = async (id, page = 1, limit = BATCH_DETAILS_LIMIT) => {
    try {
      setIsDialogLoading(true);
      setOpenDialog(true);

      const res = await dispatch(
        fetchOutboxBatch({ batchId: id || batchId, page, limit })
      ).unwrap();
      setSelectedBatch(res || null);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `${error?.message}`,
          severity: "error",
        })
      );
    } finally {
      setIsDialogLoading(false);
    }
  };

  // Retry uses same API
  const handleRetry = async (id) => {
    try {
      setLoading(true);
      let res = await dispatch(retryMappingBatch(id)).unwrap();
      if (res) {
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `${error?.message}`,
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (value) => {
    setFilterPayload((prev) => ({ ...prev, page: value }));
  };

  useEffect(() => {
    handleGetStatusMapping();
  }, [handleGetStatusMapping]);

  const columns = buildColumns(handleRetry);

  return (
    <Grid container height="100vh" sx={{ overflow: "auto" }}>
      {loading && <Loader />}

      <BatchDetailsDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        batch={selectedBatch}
        isLoading={isDialogLoading}
        fetchOutBoxDetails={fetchOutBoxDetails}
        batchId={batchId}
      />

      {/* Header */}
      <Grid
        container
        alignItems="center"
        sx={{
          width: "100vw",
          backgroundColor: "#e5faeb",
          padding: 2,
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Grid item xs={2}>
          <Tooltip title="Back to home">
            <img
              src={BackButton}
              alt="Back"
              onClick={() => navigate(`/app`)}
              style={{
                cursor: "pointer",
                marginLeft: 8,
                height: 24,
              }}
            />
          </Tooltip>
        </Grid>

        <Grid item xs={8} textAlign="center">
          <Typography className="serif_display_regular_20 dark_grey">
            Workflow Task Mapping Status
          </Typography>
        </Grid>
      </Grid>

      {/* Pagination */}
      <Grid item xs={12} mt={1} mx={10}>
        <PaginationComponent
          totalCount={totalCount}
          limit={filterPayload.limit}
          page={filterPayload.page}
          onChange={handleChangePage}
        />
      </Grid>

      {/* Table */}
      <Grid item xs={12} mx={5} style={{ height: "83vh", overflow: "auto" }}>
        <DataGridComponent
          columns={columns}
          rows={data}
          onRowClick={(e) => {
            fetchOutBoxDetails(e.row.batchId);
            setBatchID(e.row.batchId);
          }}
        />
      </Grid>
    </Grid>
  );
};

export default WorkflowMappingStatus;
